/**
 * The rule set: every number and every table that decides a recommendation, under one version.
 *
 * It is data, validated when it is defined and frozen afterwards. A missing matrix cell, a stray key, a tier that is not C, N or L, an empty rationale, a points table that could produce a fraction, or match-level bands out of order all stop the rule set from existing, with every problem listed. There is no default for anything: a value is either written here or the rule set is refused. The matrix keys are checked against the live taxonomy, so a face or frame shape added later refuses v1 until a new version covers it.
 */
import { FrameShape, NAMED_FACE_SHAPES, type NamedFaceShape } from '@optiflow/types';
import { DIMENSIONS, Dimension, type MmRange } from './dimension';
import { FrameRuleSetError } from './errors';
import {
  STYLE_MATRIX_V1,
  STYLE_PRINCIPLES_V1,
  StylePrinciple,
  StyleTier,
  type StyleMatrix,
} from './style-matrix';
import { FRAME_RULES_VERSION } from './version';

/** A ratio kept as two integers, so a band boundary is compared by cross-multiplication and never through a float. */
export interface Fraction {
  readonly numerator: number;
  readonly denominator: number;
}

export interface FrameRuleSet {
  readonly version: string;
  readonly style: {
    readonly matrix: StyleMatrix;
    readonly principles: Readonly<Record<NamedFaceShape, StylePrinciple>>;
    /** Points per component tier. A single face shape counts its tier twice; a MIXED face adds its two components. */
    readonly tierPoints: Readonly<Record<StyleTier, number>>;
    /** Recommendation points for each tier point. */
    readonly pointsPerTierPoint: number;
  };
  readonly dimensions: {
    /** Split evenly across however many dimension ranges are selected, so it must divide by 1, 2 and 3. */
    readonly maxPoints: number;
    /** The same limits the catalog accepts for a frame. */
    readonly boundsMm: Readonly<Record<Dimension, MmRange>>;
    /** Default selectable size tolerances for the reference-size shortcut. Not validated fitting tolerances. */
    readonly defaultTolerancesMm: Readonly<Record<Dimension, number>>;
  };
  readonly preferences: {
    readonly frameShape: number;
    readonly brand: number;
    readonly colour: number;
  };
  /** Every match-level boundary. OTHER_OPTIONS is whatever is ranked but below goodMatchAtLeast. */
  readonly matchLevels: {
    /** Minimum evidence: a frame whose activeMaxScore is below this has too little that could be checked to be given a level, so it is UNRANKED whatever its score. It measures evidence and awards no points. */
    readonly minimumActiveMaxScore: number;
    /** BEST_MATCH when score ÷ activeMaxScore reaches this. */
    readonly bestMatchAtLeast: Fraction;
    /** GOOD_MATCH when score ÷ activeMaxScore reaches this and not bestMatchAtLeast. */
    readonly goodMatchAtLeast: Fraction;
  };
}

export const FRAME_SHAPES: readonly FrameShape[] = Object.values(FrameShape);
export const STYLE_TIERS: readonly StyleTier[] = Object.values(StyleTier);
const PRINCIPLES: readonly string[] = Object.values(StylePrinciple);
const PREFERENCE_KEYS = ['frameShape', 'brand', 'colour'] as const;
const VERSION_PATTERN = /^frame-rules-v[1-9][0-9]*$/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value);

function checkKeys(
  value: unknown,
  expected: readonly string[],
  where: string,
  problems: string[],
): value is Record<string, unknown> {
  if (!isRecord(value)) {
    problems.push(`${where} must be an object`);
    return false;
  }
  for (const key of expected) {
    if (!Object.hasOwn(value, key)) problems.push(`${where} is missing ${key}`);
  }
  for (const key of Object.keys(value)) {
    if (!expected.includes(key)) problems.push(`${where} has an unexpected key ${key}`);
  }
  return true;
}

function checkFraction(value: unknown, where: string, problems: string[]): value is Fraction {
  if (!checkKeys(value, ['numerator', 'denominator'], where, problems)) return false;
  const { numerator, denominator } = value;
  const valid = isInteger(numerator) && numerator >= 0 && isInteger(denominator) && denominator > 0;
  if (!isInteger(numerator) || numerator < 0) {
    problems.push(`${where}.numerator must be a whole number ≥ 0`);
  }
  if (!isInteger(denominator) || denominator <= 0) {
    problems.push(`${where}.denominator must be a whole number > 0`);
  }
  return valid;
}

const STYLE_KEYS = ['matrix', 'principles', 'tierPoints', 'pointsPerTierPoint'];
const DIMENSION_KEYS = ['maxPoints', 'boundsMm', 'defaultTolerancesMm'];
const MATCH_LEVEL_KEYS = ['minimumActiveMaxScore', 'bestMatchAtLeast', 'goodMatchAtLeast'];
const RULE_SET_KEYS = ['version', 'style', 'dimensions', 'preferences', 'matchLevels'];

function checkStyle(style: unknown, problems: string[]): void {
  if (!checkKeys(style, STYLE_KEYS, 'style', problems)) return;

  if (checkKeys(style.matrix, NAMED_FACE_SHAPES, 'style.matrix', problems)) {
    for (const face of NAMED_FACE_SHAPES) {
      const row = style.matrix[face];
      if (row === undefined || !checkKeys(row, FRAME_SHAPES, `style.matrix.${face}`, problems)) {
        continue;
      }
      for (const frame of FRAME_SHAPES) {
        const cell = row[frame];
        const where = `style.matrix.${face}.${frame}`;
        if (cell === undefined || !checkKeys(cell, ['tier', 'rationale'], where, problems)) {
          continue;
        }
        if (!(STYLE_TIERS as readonly unknown[]).includes(cell.tier)) {
          problems.push(`${where}.tier must be C, N or L`);
        }
        if (typeof cell.rationale !== 'string' || cell.rationale.trim().length === 0) {
          problems.push(`${where}.rationale must say why`);
        }
      }
    }
  }

  if (checkKeys(style.principles, NAMED_FACE_SHAPES, 'style.principles', problems)) {
    for (const face of NAMED_FACE_SHAPES) {
      const principle = style.principles[face];
      if (principle !== undefined && !PRINCIPLES.includes(principle as string)) {
        problems.push(`style.principles.${face} is not a known principle`);
      }
    }
  }

  if (checkKeys(style.tierPoints, STYLE_TIERS, 'style.tierPoints', problems)) {
    const { C, N, L } = style.tierPoints;
    for (const [tier, points] of Object.entries({ C, N, L })) {
      if (!isInteger(points) || points < 0) {
        problems.push(`style.tierPoints.${tier} must be a whole number ≥ 0`);
      }
    }
    if (isInteger(C) && isInteger(N) && isInteger(L) && !(C > N && N > L)) {
      problems.push('style.tierPoints must rank C above N above L');
    }
  }

  if (!isInteger(style.pointsPerTierPoint) || style.pointsPerTierPoint <= 0) {
    problems.push('style.pointsPerTierPoint must be a whole number > 0');
  }
}

function checkDimensions(dimensions: unknown, problems: string[]): void {
  if (!checkKeys(dimensions, DIMENSION_KEYS, 'dimensions', problems)) return;

  const { maxPoints } = dimensions;
  if (!isInteger(maxPoints) || maxPoints <= 0 || maxPoints % 6 !== 0) {
    problems.push('dimensions.maxPoints must be a whole number > 0 that divides by 1, 2 and 3');
  }

  const bounds = checkKeys(dimensions.boundsMm, DIMENSIONS, 'dimensions.boundsMm', problems)
    ? dimensions.boundsMm
    : null;
  if (bounds) {
    for (const dimension of DIMENSIONS) {
      const range = bounds[dimension];
      const where = `dimensions.boundsMm.${dimension}`;
      if (range === undefined || !checkKeys(range, ['minMm', 'maxMm'], where, problems)) continue;
      if (
        !isInteger(range.minMm) ||
        !isInteger(range.maxMm) ||
        !(0 < range.minMm && range.minMm < range.maxMm)
      ) {
        problems.push(`${where} must be whole millimetres with 0 < minMm < maxMm`);
      }
    }
  }

  if (
    checkKeys(
      dimensions.defaultTolerancesMm,
      DIMENSIONS,
      'dimensions.defaultTolerancesMm',
      problems,
    )
  ) {
    for (const dimension of DIMENSIONS) {
      const tolerance = dimensions.defaultTolerancesMm[dimension];
      const range = bounds?.[dimension];
      if (!isInteger(tolerance) || tolerance < 0) {
        problems.push(`dimensions.defaultTolerancesMm.${dimension} must be whole millimetres ≥ 0`);
      } else if (
        isRecord(range) &&
        isInteger(range.minMm) &&
        isInteger(range.maxMm) &&
        tolerance >= range.maxMm - range.minMm
      ) {
        problems.push(
          `dimensions.defaultTolerancesMm.${dimension} must be narrower than its bounds`,
        );
      }
    }
  }
}

function checkPreferences(preferences: unknown, problems: string[]): void {
  if (!checkKeys(preferences, PREFERENCE_KEYS, 'preferences', problems)) return;
  for (const key of PREFERENCE_KEYS) {
    const points = preferences[key];
    if (!isInteger(points) || points < 0) {
      problems.push(`preferences.${key} must be a whole number ≥ 0`);
    }
  }
}

function checkMatchLevels(matchLevels: unknown, problems: string[]): void {
  if (!checkKeys(matchLevels, MATCH_LEVEL_KEYS, 'matchLevels', problems)) return;

  if (!isInteger(matchLevels.minimumActiveMaxScore) || matchLevels.minimumActiveMaxScore < 1) {
    problems.push('matchLevels.minimumActiveMaxScore must be a whole number ≥ 1');
  }
  const best = checkFraction(matchLevels.bestMatchAtLeast, 'matchLevels.bestMatchAtLeast', problems)
    ? matchLevels.bestMatchAtLeast
    : null;
  const good = checkFraction(matchLevels.goodMatchAtLeast, 'matchLevels.goodMatchAtLeast', problems)
    ? matchLevels.goodMatchAtLeast
    : null;
  if (best && best.numerator > best.denominator) {
    problems.push('matchLevels.bestMatchAtLeast must not exceed 1');
  }
  if (good && good.numerator === 0) problems.push('matchLevels.goodMatchAtLeast must be above 0');
  if (best && good && good.numerator * best.denominator >= best.numerator * good.denominator) {
    problems.push('matchLevels.goodMatchAtLeast must be below matchLevels.bestMatchAtLeast');
  }
}

/** Every problem with a candidate rule set; an empty list means it may be used. */
export function ruleSetProblems(candidate: unknown): string[] {
  const problems: string[] = [];
  if (!checkKeys(candidate, RULE_SET_KEYS, 'rule set', problems)) return problems;
  if (typeof candidate.version !== 'string' || !VERSION_PATTERN.test(candidate.version)) {
    problems.push('version must look like frame-rules-v1');
  }
  checkStyle(candidate.style, problems);
  checkDimensions(candidate.dimensions, problems);
  checkPreferences(candidate.preferences, problems);
  checkMatchLevels(candidate.matchLevels, problems);
  return problems;
}

function deepFreeze<T>(value: T): T {
  if (typeof value === 'object' && value !== null && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const child of Object.values(value)) deepFreeze(child);
  }
  return value;
}

/** Validates a rule set and freezes it. A rule set with any problem is refused outright. */
export function defineFrameRuleSet(candidate: FrameRuleSet): FrameRuleSet {
  const problems = ruleSetProblems(candidate);
  if (problems.length > 0) throw new FrameRuleSetError(problems);
  return deepFreeze(candidate);
}

/** The most style points any frame can earn: a single shape's tier counted twice, which is also where MIXED is capped. */
export const styleCapTierPoints = (rules: FrameRuleSet): number => 2 * rules.style.tierPoints.C;

export const styleMaxPoints = (rules: FrameRuleSet): number =>
  styleCapTierPoints(rules) * rules.style.pointsPerTierPoint;

export const FRAME_RULES_V1: FrameRuleSet = defineFrameRuleSet({
  version: FRAME_RULES_VERSION,
  style: {
    matrix: STYLE_MATRIX_V1,
    principles: STYLE_PRINCIPLES_V1,
    tierPoints: { [StyleTier.C]: 2, [StyleTier.N]: 1, [StyleTier.L]: 0 },
    pointsPerTierPoint: 10,
  },
  dimensions: {
    maxPoints: 30,
    boundsMm: {
      [Dimension.LENS_WIDTH]: { minMm: 30, maxMm: 80 },
      [Dimension.BRIDGE_WIDTH]: { minMm: 10, maxMm: 35 },
      [Dimension.TEMPLE_LENGTH]: { minMm: 100, maxMm: 200 },
    },
    defaultTolerancesMm: {
      [Dimension.LENS_WIDTH]: 2,
      [Dimension.BRIDGE_WIDTH]: 1,
      [Dimension.TEMPLE_LENGTH]: 5,
    },
  },
  preferences: { frameShape: 8, brand: 6, colour: 6 },
  matchLevels: {
    minimumActiveMaxScore: 20,
    bestMatchAtLeast: { numerator: 1, denominator: 1 },
    goodMatchAtLeast: { numerator: 1, denominator: 2 },
  },
});
